import React, { useState } from 'react';
import './TradesPage.css';
import AdminLayout from '../AdminLayout';
import { useGetAdminTradesQuery } from 'store/services/trade/tradeApi';
import { Points } from 'components/Points/Points';
import { getAvatarPlaceholder } from 'utils/avatarUtils';

const TradesPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [page] = useState(1); // TODO: Implement pagination when needed

  // Fetch trades from API
  const { data, isLoading, error } = useGetAdminTradesQuery({
    page,
    pageSize: 50,
    search: searchTerm
  });

  // Format date to MM.DD.YYYY
  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${month}.${day}.${year}`;
  };

  // Get status badge color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return '#10b981'; // Green
      case 'transit':
        return '#f59e0b'; // Orange
      case 'ready':
        return '#3b82f6'; // Blue
      default:
        return '#999';
    }
  };

  // Format status display
  const formatStatus = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'transit':
        return 'In Transit';
      case 'ready':
        return 'Ready to Ship';
      default:
        return status;
    }
  };

  // Render user avatar with fallback
  const renderAvatar = (username: string, avatarUrl?: string) => {
    const avatarPlaceholder = getAvatarPlaceholder(username);
    const hasValidAvatar = avatarUrl && avatarUrl !== '' &&
                           avatarUrl !== 'null' && avatarUrl !== 'undefined';

    return (
      <>
        {hasValidAvatar ? (
          <img
            src={avatarUrl}
            alt={username}
            className="user-avatar"
            onError={(e) => {
              const target = e.currentTarget;
              target.style.display = 'none';
              const placeholder = target.nextElementSibling as HTMLElement;
              if (placeholder) {
                placeholder.style.display = 'flex';
              }
            }}
          />
        ) : null}
        {avatarPlaceholder ? (
          <div
            className="user-avatar user-avatar-placeholder"
            style={{
              backgroundColor: avatarPlaceholder.color,
              display: hasValidAvatar ? 'none' : 'flex'
            }}
          >
            <span className="user-avatar-letter">
              {avatarPlaceholder.letter}
            </span>
          </div>
        ) : (
          <div
            className="user-avatar user-avatar-placeholder"
            style={{
              backgroundColor: '#8409D3',
              display: hasValidAvatar ? 'none' : 'flex'
            }}
          >
            <span className="user-avatar-letter">?</span>
          </div>
        )}
      </>
    );
  };

  // Get trades from API response
  const trades = data?.trades || [];
  
  // No need to filter client-side since API handles search
  const filteredTrades = trades;

  // Handle loading state
  if (isLoading) {
    return (
      <AdminLayout title="Trades" currentPath="/admin/trades">
        <div className="trades-page">
          <div className="trades-header">
            <h1 className="trades-title">Trades</h1>
          </div>
          <div className="trades-table-container">
            <div className="trades-empty">Loading trades...</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  // Handle error state
  if (error) {
    return (
      <AdminLayout title="Trades" currentPath="/admin/trades">
        <div className="trades-page">
          <div className="trades-header">
            <h1 className="trades-title">Trades</h1>
          </div>
          <div className="trades-table-container">
            <div className="trades-empty">Error loading trades. Please try again later.</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Trades" currentPath="/admin/trades">
      <div className="trades-page">
        <div className="trades-header">
          <h1 className="trades-title">Trades</h1>
          
          <div className="trades-controls">
            <input
              type="text"
              className="trades-search-input"
              placeholder="Search by username..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="trades-table-container">
          <table className="trades-table">
            <thead>
              <tr>
                <th>Sender</th>
                <th>Value</th>
                <th>Receiver</th>
                <th>Initiated At</th>
                <th>Shipped At</th>
                <th>Completed At</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredTrades.length === 0 ? (
                <tr>
                  <td colSpan={7} className="trades-empty">
                    {searchTerm ? `No trades found for "${searchTerm}"` : 'No trades available'}
                  </td>
                </tr>
              ) : (
                filteredTrades.map((trade) => (
                  <tr key={trade.id}>
                    <td>
                      <div className="trades-user">
                        {renderAvatar(trade.senderUsername, trade.senderAvatar)}
                        <span className="trades-username">{trade.senderUsername}</span>
                      </div>
                    </td>
                    <td>
                      <Points points={trade.totalValue} type="small" />
                    </td>
                    <td>
                      <div className="trades-user">
                        {renderAvatar(trade.receiverUsername, trade.receiverAvatar)}
                        <span className="trades-username">{trade.receiverUsername}</span>
                      </div>
                    </td>
                    <td className="trades-date">{formatDate(trade.initiatedAt)}</td>
                    <td className="trades-date">{formatDate(trade.shippedAt)}</td>
                    <td className="trades-date">{formatDate(trade.completedAt)}</td>
                    <td>
                      <span 
                        className="trades-status-badge"
                        style={{ 
                          backgroundColor: getStatusColor(trade.status),
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          color: '#fff'
                        }}
                      >
                        {formatStatus(trade.status)}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {data && data.totalCount > 0 && (
          <div className="trades-pagination">
            <span className="trades-pagination-info">
              Showing {filteredTrades.length} of {data.totalCount} trade{data.totalCount !== 1 ? 's' : ''}
            </span>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default TradesPage;